import React, { useState, useMemo, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Search, Grid, List, ChevronRight } from 'lucide-react';
import catalogueStructure from '../data/catalogueStructure';
import CatalogueSidebar from '../components/CatalogueSidebar';
import { ProductCardGrid, ProductCardList } from '../components/CatalogueProductCards';
import CatalogueSeoText from '../components/CatalogueSeoText';
import CatalogueJsonLd from '../components/CatalogueJsonLd';

const CataloguePage = () => {
  const { category, subcategory } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState(
    category ? [category] : ['barnums-tentes']
  );

  useEffect(() => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`)
      .then(res => res.json())
      .then(data => { setAllProducts(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const currentCategory = category
    ? catalogueStructure.find(c => c.slug === category)
    : null;

  const currentSubcategory = subcategory && currentCategory
    ? currentCategory.subcategories.find(s => s.slug === subcategory)
    : null;

  const filteredProducts = useMemo(() => {
    let products = allProducts;

    if (currentCategory) {
      products = products.filter(p => p.category === currentCategory.slug);
    }

    if (currentSubcategory) {
      products = products.filter(p => p.subcategory === currentSubcategory.slug);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      products = products.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q)
      );
    }

    return products;
  }, [currentCategory, currentSubcategory, searchQuery, allProducts]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const productCount = filteredProducts.length;

  return (
    <div className="min-h-screen bg-gray-50 pt-24" data-testid="catalogue-page">
      <CatalogueJsonLd
        currentCategory={currentCategory}
        currentSubcategory={currentSubcategory}
        products={filteredProducts}
      />
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center gap-2 text-sm flex-wrap" data-testid="breadcrumb">
            <Link to="/" className="text-gray-500 hover:text-orange-600">Accueil</Link>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <Link
              to="/catalogue"
              className={currentCategory ? 'text-gray-500 hover:text-orange-600' : 'text-orange-600 font-medium'}
            >
              Catalogue
            </Link>
            {currentCategory && (
              <>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <Link
                  to={`/catalogue/${currentCategory.slug}`}
                  className={currentSubcategory ? 'text-gray-500 hover:text-orange-600' : 'text-orange-600 font-medium'}
                >
                  {currentCategory.name}
                </Link>
              </>
            )}
            {currentSubcategory && (
              <>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <span className="text-orange-600 font-medium">{currentSubcategory.name}</span>
              </>
            )}
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <CatalogueSidebar
            currentCategory={currentCategory}
            currentSubcategory={currentSubcategory}
            expandedCategories={expandedCategories}
            toggleCategory={toggleCategory}
          />

          {/* Main Content */}
          <main className="flex-1">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900" data-testid="catalogue-title">
                  {currentSubcategory ? currentSubcategory.name : currentCategory ? currentCategory.name : 'Tout le Catalogue'}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {loading ? 'Chargement...' : `${productCount} produit${productCount > 1 ? 's' : ''}`}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative flex-1 sm:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                    data-testid="catalogue-search"
                  />
                </div>
                <div className="hidden sm:flex border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'bg-orange-100 text-orange-600' : 'text-gray-400 hover:text-gray-600'}`}
                    data-testid="view-grid"
                  >
                    <Grid className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-orange-100 text-orange-600' : 'text-gray-400 hover:text-gray-600'}`}
                    data-testid="view-list"
                  >
                    <List className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20" data-testid="loading-spinner">
                <div className="w-10 h-10 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin"></div>
              </div>
            ) : filteredProducts.length > 0 ? (
              viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
                  {filteredProducts.map((product) => (
                    <ProductCardGrid key={product.id} product={product} />
                  ))}
                </div>
              ) : (
                <div className="space-y-4" data-testid="products-list">
                  {filteredProducts.map((product) => (
                    <ProductCardList key={product.id} product={product} />
                  ))}
                </div>
              )
            ) : (
              <div className="text-center py-16 bg-white rounded-xl" data-testid="no-products">
                <p className="text-gray-500 text-lg">Aucun produit trouvé.</p>
                <p className="text-gray-400 mt-2">Essayez de modifier votre recherche ou de changer de catégorie.</p>
              </div>
            )}

            <CatalogueSeoText
              currentCategory={currentCategory}
              currentSubcategory={currentSubcategory}
            />
          </main>
        </div>
      </div>
    </div>
  );
};

export default CataloguePage;
